import moment from 'moment-timezone';
import { DATE_SHORTCUTS } from '@utils/constants';

export const FIVE_MIN = 5;
export const FIFTEEN_MIN = 15;
export const THIRTY_MIN = 30;
export const HOUR = 60;
export const SIX_HOURS = 60 * 6;
export const DAY = 60 * 24;
export const WEEK = 60 * 24 * 7;
export const MONTH = 60 * 24 * 31;
export const THREE_MONTHS = 60 * 24 * 31 * 3;
export const YEAR = 60 * 24 * 365;
const VIEWER_DRILL_DOWN_LIMIT_DAYS = 60 * 24 * 31;

export const GRANULARITY = {
  PT10S: 'PT10S',
  PT1M: 'PT1M',
  PT5M: 'PT5M',
  PT15M: 'PT15M',
  PT30M: 'PT30M',
  PT1H: 'PT1H',
  PT6H: 'PT6H',
  PT12H: 'PT12H',
  P1D: 'P1D',
  P1W: 'P1W',
  P1M: 'P1M',
  P30D: 'P30D',
};

export const GRANULARITY_UNIT = {
  [GRANULARITY.PT1S]: 'second',
  [GRANULARITY.PT1M]: 'minute',
  [GRANULARITY.PT1H]: 'hour',
  [GRANULARITY.P1D]: 'day',
};

export function getIntervalDifferenceLabel(interval) {
  const durationInMillis = moment(interval.to).diff(moment(interval.from));
  const totalDays = Math.floor(durationInMillis / (1000 * 60 * 60 * 24));
  const remainingHours = Math.floor((durationInMillis % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const remainingMinutes = Math.floor((durationInMillis % (1000 * 60 * 60)) / (1000 * 60));
  let formattedDuration = '';

  if (totalDays > 0) {
    formattedDuration += `${totalDays}d `;
  }
  if (remainingHours > 0) {
    formattedDuration += `${remainingHours}h `;
  }
  if (remainingMinutes > 0) {
    formattedDuration += `${remainingMinutes}m `;
  }

  return formattedDuration.trim();
}

function getGranularity(from, to, minuteGranularity = true, overview = false, shortcut = null) {
  // use minutes otherwise there will be rounding issue
  const diffInMins = moment(to).diff(moment(from), 'minutes');
  let validGranularity;
  let fromTillNow;
  switch (true) {
    case shortcut && shortcut === DATE_SHORTCUTS.last15Mins:
      validGranularity = GRANULARITY.PT10S;
      break;
    case diffInMins <= SIX_HOURS: // Six Hours, added for Overview Dashboard
      fromTillNow = moment().diff(from, 'minutes');
      if (fromTillNow > MONTH) {
        validGranularity = GRANULARITY.PT1H;
      } else {
        validGranularity = minuteGranularity ? GRANULARITY.PT1M : GRANULARITY.PT1H;
      }
      break;
    case diffInMins <= DAY: // <= 1 day
      fromTillNow = moment().diff(from, 'minutes');
      if (fromTillNow > MONTH) {
        validGranularity = GRANULARITY.PT1H;
      } else if (overview) {
        validGranularity = GRANULARITY.PT1H;
      } else {
        validGranularity =
          minuteGranularity && !window.isGlobalAccount ? GRANULARITY.PT1M : GRANULARITY.PT1H;
      }
      break;
    case diffInMins <= MONTH: // > 1 day and <= 1 month
      validGranularity = GRANULARITY.PT1H;
      break;
    case diffInMins <= YEAR: // > 1 month and <= 1 year
      validGranularity = GRANULARITY.P1D;
      break;
    default:
      validGranularity = GRANULARITY.P1D;
  }
  return validGranularity;
}

export function getAppDefaultGranularity(
  from,
  to,
  minuteGranularity = true,
  overview = false,
  shortcut = null,
  dashboardType = 'trends',
) {
  let granularity = getGranularity(from, to, minuteGranularity, overview, shortcut);
  if (granularity === GRANULARITY.PT1M && dashboardType === 'trends') {
    // Show 5mins granularity for ECO by default
    granularity = GRANULARITY.PT5M;
  }
  return granularity;
}

export const getIntervalByGranularity = (interval, granularity) => {
  let { from, to } = interval;
  switch (granularity) {
    case GRANULARITY.PT1M:
      from = moment(from).startOf(GRANULARITY_UNIT.PT1M).format();
      to = moment(to).endOf(GRANULARITY_UNIT.PT1M).format();
      break;
    case GRANULARITY.PT1H:
      from = moment(from).startOf(GRANULARITY_UNIT.PT1H).format();
      to = moment(to).endOf(GRANULARITY_UNIT.PT1H).format();
      break;
    case GRANULARITY.P1D:
      from = moment(from).startOf(GRANULARITY_UNIT.P1D).format();
      to = moment(to).endOf(GRANULARITY_UNIT.P1D).format();
      break;
    default:
      break;
  }

  return { from, to };
};

export default getGranularity;

export const moreThanViewerDrillDownLimit = (startTime) =>
  moment().diff(moment(startTime), 'minutes') > VIEWER_DRILL_DOWN_LIMIT_DAYS;
